import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import ReactSelect, { SingleValue } from "react-select";
import { toast } from "react-toastify";
import PageHeader from "../../components/PageHeader";
import { useAuth } from "../../context/AuthContext";
import { hasFullAccessUser } from "../../lib/screenAccess";
import { useLojas } from "../../hooks/useLojas";
import { filaPizzasService } from "../../services/filaPizzasService";
import {
  LABEL_CLASS,
  SELECT_STYLES,
} from "../../components/DateFilters/constants";
import { useFilaIntegracaoEvents } from "../../hooks/useFilaIntegracaoEvents";
import { FILA_INTEGRACAO_SSE_QUERY_KEYS } from "../../constants/rankingEventQueryKeys";

type RodizioTipo = "salgada" | "doce";

interface LojaOption {
  value: number;
  label: string;
}

const RodizioUrgenciaPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canPickLoja = hasFullAccessUser(user);
  const { data: lojas = [] } = useLojas();
  const [tipo, setTipo] = useState<RodizioTipo>("salgada");
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    user?.lojaId
  );
  const [pendingSabor, setPendingSabor] = useState<string | null>(null);

  useEffect(() => {
    if (selectedLojaId === undefined && user?.lojaId != null) {
      setSelectedLojaId(user.lojaId);
    }
  }, [user?.lojaId, selectedLojaId]);

  const effectiveLojaId = canPickLoja
    ? (selectedLojaId ?? user?.lojaId)
    : user?.lojaId;

  useFilaIntegracaoEvents(effectiveLojaId, {
    queryKeys: FILA_INTEGRACAO_SSE_QUERY_KEYS.rodizioUrgencia,
  });

  const lojaOptions = useMemo<LojaOption[]>(
    () => lojas.map((loja) => ({ value: loja.id, label: loja.nome })),
    [lojas]
  );
  const selectedLojaOption =
    lojaOptions.find((o) => o.value === selectedLojaId) ?? null;

  const { data: sabores = [], isLoading: isLoadingSabores } = useQuery({
    queryKey: ["rodizio-sabores", tipo],
    queryFn: () => filaPizzasService.listRodizioSabores(tipo),
  });

  const { data: urgencias = [], isLoading: isLoadingUrgencias } = useQuery({
    queryKey: ["rodizio-urgencias", tipo, effectiveLojaId],
    queryFn: () =>
      filaPizzasService.listRodizioUrgencias(tipo, effectiveLojaId),
    enabled: effectiveLojaId != null,
  });

  const criarUrgenciaMutation = useMutation({
    mutationFn: (saborNome: string) =>
      filaPizzasService.criarRodizioUrgencia({
        tipo,
        saborNome,
        lojaId: effectiveLojaId,
      }),
    onSuccess: (_data, saborNome) => {
      toast.success(
        `${saborNome} na urgência. Depois a sequência segue de onde estava.`
      );
      queryClient.invalidateQueries({ queryKey: ["rodizio-urgencias"] });
      queryClient.invalidateQueries({ queryKey: ["rodizio-estado"] });
    },
    onError: (error: { message?: string }) => {
      toast.error(error?.message || "Erro ao criar urgência de rodízio.");
    },
    onSettled: () => setPendingSabor(null),
  });

  const handleClickSabor = (sabor: string) => {
    if (effectiveLojaId == null) {
      toast.error("Selecione uma loja.");
      return;
    }
    setPendingSabor(sabor);
    criarUrgenciaMutation.mutate(sabor);
  };

  return (
    <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title="Rodízio — Urgência"
        description="Clique em um sabor para enviá-lo já à fila como urgência. Ao concluir, a produção retoma a sequência normal de onde parou."
      />

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setTipo("salgada")}
            className={`rounded-md px-4 py-2 text-sm font-medium ${
              tipo === "salgada"
                ? "bg-primary text-white"
                : "border border-neutral-300 bg-white text-neutral-700"
            }`}
          >
            Salgado
          </button>
          <button
            type="button"
            onClick={() => setTipo("doce")}
            className={`rounded-md px-4 py-2 text-sm font-medium ${
              tipo === "doce"
                ? "bg-primary text-white"
                : "border border-neutral-300 bg-white text-neutral-700"
            }`}
          >
            Doce
          </button>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          {canPickLoja && (
            <div className="min-w-[220px]">
              <label className={LABEL_CLASS}>Loja</label>
              <ReactSelect
                options={lojaOptions}
                value={selectedLojaOption}
                onChange={(option: SingleValue<LojaOption>) =>
                  setSelectedLojaId(option?.value)
                }
                styles={SELECT_STYLES}
                placeholder="Selecione a loja"
              />
            </div>
          )}
          <Link
            to="/rodizio"
            className="inline-flex items-center justify-center rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          >
            Ver sequência
          </Link>
        </div>
      </div>

      <section className="mb-8">
        <h2 className="mb-3 text-base font-semibold text-neutral-800">
          Urgências pendentes
        </h2>
        <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
          {isLoadingUrgencias ? (
            <p className="text-sm text-neutral-500">Carregando...</p>
          ) : urgencias.length === 0 ? (
            <p className="text-sm text-neutral-500">
              Nenhuma urgência pendente nesta fila.
            </p>
          ) : (
            <ul className="space-y-2">
              {urgencias.map((item) => (
                <li
                  key={item.id}
                  className="flex items-center justify-between rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900"
                >
                  <span className="font-medium">{item.saborNome}</span>
                  <span className="text-xs">#{item.displayId}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-base font-semibold text-neutral-800">
          Sabores — clique para urgência
        </h2>
        {isLoadingSabores ? (
          <p className="text-sm text-neutral-500">Carregando sabores...</p>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {sabores.map((sabor) => {
              const isPending =
                pendingSabor === sabor.nome && criarUrgenciaMutation.isPending;
              return (
                <button
                  key={sabor.id}
                  type="button"
                  disabled={criarUrgenciaMutation.isPending}
                  onClick={() => handleClickSabor(sabor.nome)}
                  className="rounded-lg border border-neutral-200 bg-white px-4 py-3 text-left text-sm font-medium text-neutral-800 shadow-sm transition hover:border-primary hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isPending ? "Enviando..." : sabor.nome}
                </button>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
};

export default RodizioUrgenciaPage;
